'use client'

import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import { cn } from '@mntn-dev/ui-utilities'
import { AnimatePresence, motion } from 'motion/react'
import { Fragment } from 'react'
import { getInputStyles } from '../../styles/input.ts'
import { Icon } from '../icon/icon.tsx'
import { PopoverListener } from '../popover/popover-listener.tsx'
import { SelectOptions } from './select-options.tsx'
import { SelectSearch } from './select-search.tsx'
import { SelectValues } from './select-values.tsx'
import type { SelectOptionData, SelectProps } from './types.ts'
import { useSelectInstance } from './use-select.ts'

const popoutVariants = {
  init: {
    opacity: 0,
    scale: 0.95,
  },
  open: {
    opacity: 1,
    scale: 1,
  },
  close: {
    opacity: 0,
    scale: 0.95,
    transition: {
      delay: 0.15,
    },
  },
} as const

const Select = <ValueType extends string = string>({
  className,
  ref,
  ...props
}: SelectProps<ValueType>) => {
  const [context, SelectProvider] = useSelectInstance<ValueType>(props)

  const {
    hasError,
    hasSuccess,
    hasWarning,
    disabled,
    readOnly,
    ...formFieldProps
  } = context.formFieldProps

  const { onAnimationComplete, onOpen } = context

  const inputStyles = getInputStyles(context.formFieldProps, true)

  return (
    <SelectProvider value={context}>
      <Popover>
        {({ open, close }) => (
          <>
            <PopoverListener open={open} onOpen={onOpen} />
            <PopoverButton as={Fragment} disabled={disabled || readOnly}>
              <div
                ref={ref}
                tabIndex={0}
                // biome-ignore lint/a11y/useSemanticElements: using div instead
                role="listbox"
                className={cn(
                  inputStyles,
                  'relative pr-11',
                  {
                    'cursor-pointer': !(disabled || readOnly),
                  },
                  className
                )}
                {...formFieldProps}
              >
                <SelectValues />
                <span
                  className={cn(
                    'absolute right-3 h-5 w-5 flex justify-center items-center shrink-0',
                    {
                      'opacity-20': disabled || readOnly,
                    }
                  )}
                >
                  <Icon name="ChevronDownIcon" size="md" color="info" />
                </span>
              </div>
            </PopoverButton>
            <AnimatePresence>
              {open && (
                <PopoverPanel
                  static
                  as={motion.div}
                  initial="init"
                  animate="open"
                  exit="close"
                  variants={popoutVariants}
                  anchor={{
                    to: 'bottom start',
                    gap: '16px',
                    padding: '32px',
                  }}
                  className="popover-panel"
                  onAnimationComplete={onAnimationComplete}
                >
                  <SelectSearch />
                  <SelectOptions close={close} />
                </PopoverPanel>
              )}
            </AnimatePresence>
          </>
        )}
      </Popover>
    </SelectProvider>
  )
}

export { Select, type SelectOptionData as SelectOption, type SelectProps }
